<template>
  <BaseLayout
    title="Cycle Time Calculator"
    subtitle="Estimate robot motion + overheads (budgetary)"
    :narrow="true"
  >
    <div class="card space-y-8">
      <!-- Form -->
      <form @submit.prevent="onCalculate" novalidate class="space-y-6">
        <div class="grid gap-4 md:grid-cols-2">
          <!-- Robot -->
          <!-- Robot -->
<label class="field">
  <span>Robot</span>
  <select v-model="robotId" class="input">
    <!-- Dynamically render robots loaded from Firestore (or local fallback) -->
    <option
      v-for="(r, id) in robotsMap"
      :key="id"
      :value="id"
    >
      {{ r.name || r.model || id }}
    </option>
  </select>
</label>


          <!-- Profile -->
         <label class="field">
  <span>Profile</span>
  <select v-model="profile" class="input">
    <option value="auto">Auto (recommended)</option>
    <option value="trapezoidal">Trapezoidal</option>
    <option value="triangular">Triangular</option>
  </select>
</label>


          <label class="field">
            <span>Move Distance (mm)</span>
            <input v-model.number="distance" type="number" min="0" step="1" class="input" />
          </label>

          <label class="field">
            <span>Payload (kg)</span>
            <input v-model.number="payload" type="number" min="0" step="0.01" class="input" />
          </label>

          <label class="field md:col-span-2">
            <span>Speed Override (mm/s) — optional</span>
            <input
              v-model.number="userSpeed"
              type="number"
              min="0"
              step="1"
              class="input"
              placeholder="Leave blank to use robot rated"
            />
          </label>

          <label class="field md:col-span-2">
            <span>Accel Override (mm/s²) — optional</span>
            <input
              v-model.number="userAccel"
              type="number"
              min="0"
              step="1"
              class="input"
              placeholder="Leave blank to use robot rated"
            />
          </label>
        </div>

        <div>
          <h3 class="section-heading">Delays (seconds)</h3>
          <div class="grid gap-4 md:grid-cols-3">
            <label class="field"><span>Approach</span><input v-model.number="delays.approach" type="number" step="0.01" min="0" class="input" /></label>
            <label class="field"><span>Settle</span><input v-model.number="delays.settle" type="number" step="0.01" min="0" class="input" /></label>
            <label class="field"><span>Gripper Open</span><input v-model.number="delays.gripperOpen" type="number" step="0.01" min="0" class="input" /></label>
            <label class="field"><span>Gripper Close</span><input v-model.number="delays.gripperClose" type="number" step="0.01" min="0" class="input" /></label>
            <label class="field"><span>Vision</span><input v-model.number="delays.vision" type="number" step="0.01" min="0" class="input" /></label>
            <label class="field"><span>I/O</span><input v-model.number="delays.io" type="number" step="0.01" min="0" class="input" /></label>
            <label class="field"><span>Custom</span><input v-model.number="delays.custom" type="number" step="0.01" min="0" class="input" /></label>
          </div>
        </div>
        <div v-if="mode === 'pickplace'">
  <h3 class="section-heading mt-6">Return Leg</h3>
  <div class="grid gap-4 md:grid-cols-2">
    <label class="field"><span>Return Distance (mm)</span><input v-model.number="distanceB" type="number" min="0" step="1" class="input" /></label>
    <label class="field"><span>Return Payload (kg)</span><input v-model.number="payloadB" type="number" min="0" step="0.01" class="input" /></label>
  </div>
  <details class="mt-2">
    <summary class="cursor-pointer text-sm font-semibold">Return Leg Delays</summary>
    <div class="grid gap-4 md:grid-cols-3 mt-3">
      <label class="field"><span>Approach</span><input v-model.number="delaysB.approach" type="number" step="0.01" min="0" class="input" /></label>
      <label class="field"><span>Settle</span><input v-model.number="delaysB.settle" type="number" step="0.01" min="0" class="input" /></label>
      <label class="field"><span>Gripper Open</span><input v-model.number="delaysB.gripperOpen" type="number" step="0.01" min="0" class="input" /></label>
      <label class="field"><span>Gripper Close</span><input v-model.number="delaysB.gripperClose" type="number" step="0.01" min="0" class="input" /></label>
      <label class="field"><span>Vision</span><input v-model.number="delaysB.vision" type="number" step="0.01" min="0" class="input" /></label>
      <label class="field"><span>I/O</span><input v-model.number="delaysB.io" type="number" step="0.01" min="0" class="input" /></label>
      <label class="field"><span>Custom</span><input v-model.number="delaysB.custom" type="number" step="0.01" min="0" class="input" /></label>
    </div>
  </details>
</div>

<!-- Mode toggle -->
<div class="actions">
  <label><input type="radio" v-model="mode" value="single"> Single move</label>
  <label class="ml-4"><input type="radio" v-model="mode" value="pickplace"> Pick & Place (includes return)</label>
</div>

<!-- S-curve approximation -->
<div class="actions">
  <label>
    <input type="checkbox" v-model="sCurveApprox">
    Use jerk-limited S-curve approximation (+{{ (sCurvePenalty*100).toFixed(0) }}%)
  </label>
</div>

        <div class="actions">
          <button type="submit" class="btn-primary">Calculate</button>
          <!-- (Later) <button type="button" class="btn-secondary">AI Autofill Assumptions</button> -->
        </div>
      </form>

      <!-- Results -->
      <div v-if="result !== null" class="result-card">
        <h3 class="result-title">Estimated Cycle Time</h3>
        <p class="result-value">{{ result.toFixed(3) }} s</p>
        <p class="note">Includes motion + all delays.</p>
      </div>

      <p class="disclaimer">
        Budgetary estimate only. Actual performance depends on tuning, path geometry,
        fixtures, environment, and vendor validation.
      </p>
    </div>
  </BaseLayout>
</template>


<script setup>
import { onMounted, ref } from 'vue'
import { listRobots } from '@/lib/robots'
import { loadRobotsFromFirestore } from '@/lib/robotsService'

// reactive map of robots (id -> robot)
const robotsMap = ref(listRobots())
import BaseLayout from '@/components/BaseLayout.vue'
import { calcCycleTime, calcPickPlaceCycle } from '@/lib/cycleMath'

const robotId = ref('epson-t3b')
const profile = ref('auto')     // (optional: change to 'auto' if you add an Auto option)
const distance = ref(300)
const payload  = ref(1.0)
const userSpeed = ref(undefined)
const userAccel = ref(undefined)

/* ---- Base (pick) leg delays — declare BEFORE delaysB uses it ---- */
const delays = ref({
  approach: 0.10,
  settle: 0.05,
  gripperOpen: 0.10,
  gripperClose: 0.10,
  vision: 0,
  io: 0.02,
  custom: 0,
})

onMounted(async () => {
  const loaded = await loadRobotsFromFirestore()
  if (loaded) {
    robotsMap.value = loaded
    // If current default isn't present in remote set, pick the first key
    if (!robotsMap.value[robotId.value]) {
      const firstId = Object.keys(robotsMap.value)[0]
      if (firstId) robotId.value = firstId
    }
  }
})


/* ---- Mode + S-curve ---- */
const mode = ref('single')               // 'single' | 'pickplace'
const sCurveApprox  = ref(false)
const sCurvePenalty = ref(0.06)

/* ---- Return (place) leg ---- */
const distanceB = ref(300)
const payloadB  = ref(0.0)
// initialize from current pick delays, then tweak for place
const delaysB = ref({
  ...delays.value,
  gripperOpen: 0.10,
  gripperClose: 0,
})

const result = ref(null)

function onCalculate() {
  if (mode.value === 'single') {
    result.value = calcCycleTime({
      robotId: robotId.value,
      distance: distance.value,
      payload:  payload.value,
      profile:  profile.value || 'auto',
      userSpeed: userSpeed.value,
      userAccel: userAccel.value,
      delays:   delays.value,
      sCurveApprox: sCurveApprox.value,
      sCurvePenalty: sCurvePenalty.value,
    })
  } else {
    result.value = calcPickPlaceCycle(
      robotId.value,
      {
        distance: distance.value,
        payload:  payload.value,
        profile:  profile.value || 'auto',
        userSpeed: userSpeed.value,
        userAccel: userAccel.value,
        delays:   delays.value,
        sCurveApprox: sCurveApprox.value,
        sCurvePenalty: sCurvePenalty.value,
      },
      {
        distance: distanceB.value,
        payload:  payloadB.value,
        profile:  profile.value || 'auto',
        userSpeed: userSpeed.value,
        userAccel: userAccel.value,
        delays:   delaysB.value,
        sCurveApprox: sCurveApprox.value,
        sCurvePenalty: sCurvePenalty.value,
      }
    )
  }
}
</script>


<style scoped>
/* --- White Card Shell --- */
.card {
  background: #ffffff;
  border: 1px solid #e5e7eb;            /* subtle border */
  border-radius: 16px;                   /* rounded corners */
  padding: 24px;                         /* inner padding */
  max-width: 980px;                      /* keep it readable */
  margin: 0 auto;                        /* center the card */
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.06);
}

/* --- Form Fields --- */
.field { display: flex; flex-direction: column; gap: 6px; }

.field > span {
  font-weight: 600;
  color: #0f172a;                        /* slate-900-ish */
}

.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;             /* slate-200 */
  border-radius: 12px;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.input:focus {
  outline: none;
  border-color: #93c5fd;                 /* blue-300 */
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15); /* soft ring */
}

.section-heading { 
  font-weight: 700; 
  margin-bottom: 8px; 
  color: #0f172a;
}

/* --- Actions --- */
.actions { 
  display: flex; 
  gap: 12px; 
  margin-top: 4px; 
}

.btn-primary {
  padding: 10px 16px; 
  border-radius: 12px;
  background: #2563eb;                   /* blue-600 */
  color: #fff; 
  border: none; 
  cursor: pointer;
  font-weight: 600;
  transition: transform .05s ease, background-color .15s ease;
}
.btn-primary:hover { background: #1d4ed8; }
.btn-primary:active { transform: translateY(1px); }

/* --- Results --- */
.result-card {
  background: #f8fafc;                   /* slate-50 */
  border: 1px solid #e2e8f0;
  border-radius: 12px; 
  padding: 16px;
}
.result-title { font-size: 1.125rem; font-weight: 700; margin: 0; color: #0f172a; }
.result-value { margin-top: 8px; font-size: 1.6rem; font-weight: 800; color: #0f172a; }
.note { margin-top: 4px; font-size: 0.875rem; color: #475569; }

/* --- Fine print --- */
.disclaimer { font-size: 0.8rem; color: #475569; }

/* --- Responsive niceties --- */
@media (min-width: 1024px) {
  .card { padding: 32px; }
}
</style>
